import { reducerWithInitialState } from 'typescript-fsa-reducers'
import { produce } from 'immer'

import { GooglePage, GooglePost } from '@3m5/sm_publish-frontend-api/models'

import { ApiError } from '../postingRoot.reducer'
import {
  confirmManualDeferredPost,
  declineManualDeferredPost,
  deleteManualDeferredPost,
  deleteGooglePost,
  getGooglePage,
  getGooglePageFeed,
  retryManualDeferredPost,
  updateDeferredPost,
} from './googleMyBusiness.actions'
import { EventType, Message, Severity } from '@professocial/professocialTypes/types/messageTypes'

export interface GoogleMyBusinessState {
  fetching: boolean
  googleMyBusinessPage?: GooglePage
  googleFeeds?: GooglePost[]
  error: ApiError
}

const INITIAL_STATE: GoogleMyBusinessState = {
  fetching: false,
  error: {},
}

const triggerProfessocialDeletePostSuccess = (data: boolean) => {
  window.dispatchEvent(new CustomEvent('ProfessocialDeletePostSuccess', { detail: data }))
}

const triggerProfessocialPostingSuccess = (data: boolean) => {
  window.dispatchEvent(new CustomEvent('ProfessocialPostingSuccess', { detail: data }))
}

const triggerProfessocialMessage = (data: Message) => {
  window.dispatchEvent(new CustomEvent('ProfessocialMessage', { detail: data }))
}

export const googleMyBusinessReducer = reducerWithInitialState(INITIAL_STATE)
  /**
   * getGooglePage
   */
  .case(getGooglePage.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      delete draft.error.getGooglePage
    })
  })

  .case(getGooglePage.done, (state, { result }) => {
    return produce(state, draft => {
      draft.googleMyBusinessPage = result
      draft.fetching = false
    })
  })

  .case(getGooglePage.failed, (state, { error }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.error.getGooglePage = (error as Response)
    })
  })

  /**
   * getGooglePageFeed
   */
  .case(getGooglePageFeed.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      delete draft.error.getGooglePageFeed
    })
  })

  .case(getGooglePageFeed.done, (state, { result }) => {
    return produce(state, draft => {
      draft.googleFeeds = result
      draft.fetching = false
    })
  })

  .case(getGooglePageFeed.failed, (state, { error }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.error.getGooglePageFeed = (error as Response)
    })
  })

/**
   * deletePost
   */
  .case(deleteGooglePost.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      delete draft.error.deleteGooglePost
    })
  })

  .case(deleteGooglePost.done, (state) => {
    return produce(state, draft => {
      draft.fetching = false
      triggerProfessocialDeletePostSuccess(true)
    })
  })

  .case(deleteGooglePost.failed, (state, { error }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.error.deleteGooglePost = (error as Response)
      triggerProfessocialMessage({
        severity: Severity.error,
        message: 'Error',
        eventType: EventType.delete,
      })
    })
  })

  /**
   * confirmManualDeferredPost
   */
  .case(confirmManualDeferredPost.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      delete draft.error.confirmManualDeferredPost
    })
  })

  .case(confirmManualDeferredPost.done, (state) => {
    return produce(state, draft => {
      draft.fetching = false
      triggerProfessocialPostingSuccess(true)
    })
  })

  .case(confirmManualDeferredPost.failed, (state, { error }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.error.confirmManualDeferredPost = (error as Response)
      triggerProfessocialMessage({
        severity: Severity.error,
        message: 'Error',
        eventType: EventType.update,
      })
    })
  })

  /**
   * declineManualDeferredPost
   */
  .case(declineManualDeferredPost.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      delete draft.error.declineManualDeferredPost
    })
  })

  .case(declineManualDeferredPost.done, (state) => {
    return produce(state, draft => {
      draft.fetching = false
      triggerProfessocialDeletePostSuccess(true)
    })
  })

  .case(declineManualDeferredPost.failed, (state, { error }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.error.declineManualDeferredPost = (error as Response)
      triggerProfessocialMessage({
        severity: Severity.error,
        message: 'Error',
        eventType: EventType.delete,
      })
    })
  })

  /**
   * deleteManualDeferredPost
   */
  .case(deleteManualDeferredPost.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      delete draft.error.deleteManualDeferredPost
    })
  })

  .case(deleteManualDeferredPost.done, (state) => {
    return produce(state, draft => {
      draft.fetching = false
      triggerProfessocialDeletePostSuccess(true)
    })
  })

  .case(deleteManualDeferredPost.failed, (state, { error }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.error.deleteManualDeferredPost = (error as Response)
      triggerProfessocialMessage({
        severity: Severity.error,
        message: 'Error',
        eventType: EventType.delete,
      })
    })
  })

  /**
   * retryManualDeferredPost
   */
  .case(retryManualDeferredPost.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      delete draft.error.retryManualDeferredPost
    })
  })

  .case(retryManualDeferredPost.done, (state) => {
    return produce(state, draft => {
      draft.fetching = false
      triggerProfessocialPostingSuccess(true)
    })
  })

  .case(retryManualDeferredPost.failed, (state, { error }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.error.retryManualDeferredPost = (error as Response)
      triggerProfessocialMessage({
        severity: Severity.error,
        message: 'Error',
        eventType: EventType.delete,
      })
    })
  })

  /**
   * updateDeferredPost
   */
  .case(updateDeferredPost.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      delete draft.error.updateDeferredPost
    })
  })

  .case(updateDeferredPost.done, (state) => {
    return produce(state, draft => {
      draft.fetching = false
    })
  })

  .case(updateDeferredPost.failed, (state, { error }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.error.updateDeferredPost = (error as Response)
      triggerProfessocialMessage({
        severity: Severity.error,
        message: 'Error',
        eventType: EventType.delete,
      })
    })
  })
